import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import './HelpPage.css';

function CoSection(props) {
  var isDark = props.isDark;
  return (
    <div className={'help-reveal help-fade-up help-delay-' + (props.delay || '1') + ' help-section-card'} style={{
      backgroundColor: isDark ? '#141414' : '#fff',
      border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8'),
      borderRadius: '16px',
      padding: '28px',
      marginBottom: '20px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
    }}>
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <div style={{
          width: '40px', height: '40px', borderRadius: '10px',
          backgroundColor: 'rgba(201,168,76,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <i className={'fa fa-' + props.icon} style={{ color: '#c9a84c', fontSize: '16px' }} />
        </div>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: isDark ? '#fff' : '#1a1a1a' }}>
          {props.title}
        </h2>
      </div>
      <div style={{ color: isDark ? '#d1d5db' : '#4b5563', fontSize: '15px', lineHeight: '1.7' }}>
        {props.children}
      </div>
    </div>
  );
}

function CoScreenshot(props) {
  var isDark = props.isDark;
  var isMobile = window.innerWidth < 768;
  var src = props.src;
  if (isMobile && isDark) {
    src = src.replace(/\.jpg$/, '-mobile-dark.jpg');
  } else if (isMobile) {
    src = src.replace(/\.jpg$/, '-mobile.jpg');
  } else if (isDark) {
    src = src.replace(/\.jpg$/, '-dark.jpg');
  }
  return (
    <div className="help-screenshot" style={{
      marginTop: '16px',
      marginBottom: '8px',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid ' + (isDark ? '#2a2a2a' : '#e2e2e2'),
      boxShadow: '0 4px 20px rgba(0,0,0,' + (isDark ? '0.3' : '0.08') + ')'
    }}>
      <img src={src} alt={props.alt || ''} style={{
        width: '100%', display: 'block'
      }} />
    </div>
  );
}

class CartOptimizer extends Component {
  constructor(props) {
    super(props);
    this.state = { isDark: document.documentElement.classList.contains('tw-dark'), showScrollTop: false };
    this._observer = null;
    this._darkObserver = null;
    this._handleScroll = this._handleScroll.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    var els = document.querySelectorAll('.help-reveal');
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('help-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
      els.forEach(function(el) { observer.observe(el); });
      this._observer = observer;
    } else {
      els.forEach(function(el) { el.classList.add('help-visible'); });
    }

    window.addEventListener('scroll', this._handleScroll);
    var self = this;
    this._darkObserver = new MutationObserver(function() {
      var dark = document.documentElement.classList.contains('tw-dark');
      if (dark !== self.state.isDark) self.setState({ isDark: dark });
    });
    this._darkObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  componentWillUnmount() {
    if (this._observer) this._observer.disconnect();
    if (this._darkObserver) this._darkObserver.disconnect();
    window.removeEventListener('scroll', this._handleScroll);
  }

  _handleScroll() {
    var show = window.scrollY > 400;
    if (show !== this.state.showScrollTop) this.setState({ showScrollTop: show });
  }

  render() {
    var isDark = this.state.isDark;

    return (
      <div style={{ backgroundColor: isDark ? '#111' : '#f9fafb', minHeight: '100vh' }}>
        <Helmet><title>Cart Optimizer | Primal Market</title></Helmet>

        {/* Hero */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          padding: '60px 20px 48px',
          textAlign: 'center'
        }}>
          <div>
            <div style={{
              width: '56px', height: '56px', borderRadius: '16px',
              backgroundColor: 'rgba(201,168,76,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <i className="fa fa-shopping-cart" style={{ color: '#c9a84c', fontSize: '24px' }} />
            </div>
            <h1 style={{ color: '#fff', fontSize: '32px', fontWeight: '700', marginBottom: '12px', marginTop: 0 }}>
              Cart Optimizer
            </h1>
            <p style={{ color: '#9ca3af', fontSize: '16px', maxWidth: '500px', margin: '0 auto', lineHeight: '1.6' }}>
              Get the best prices from the fewest sellers — saving you money on shipping.
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 16px 64px' }}>

          <CoSection isDark={isDark} icon="question-circle" title="What Is the Cart Optimizer?" delay="1">
            <p style={{ margin: '0 0 12px' }}>
              When you add cards from multiple sellers to your cart, you may end up paying shipping fees to each one. The Cart Optimizer analyzes your cart and finds the best combination of sellers to get your cards in fewer packages at the lowest total cost.
            </p>
            <p style={{ margin: 0 }}>
              It automatically groups items by seller so you can see exactly how your order will ship and what you'll pay.
            </p>
            <CoScreenshot isDark={isDark} src="/help/cart-grouped.jpg" alt="Cart with items grouped by seller" />
          </CoSection>

          <CoSection isDark={isDark} icon="cogs" title="How It Works" delay="2">
            <p style={{ margin: '0 0 16px' }}>
              The optimizer runs automatically when you view your cart:
            </p>
            <ol style={{ margin: '0 0 12px', paddingLeft: '20px' }}>
              <li style={{ marginBottom: '10px' }}>
                <strong>Seller Grouping</strong> — Items are grouped by seller. Each seller group ships as one package, so you only pay shipping once per seller.
              </li>
              <li style={{ marginBottom: '10px' }}>
                <strong>Price Comparison</strong> — If multiple sellers have the same card, the optimizer shows you who has the best price including shipping.
              </li>
              <li style={{ marginBottom: '10px' }}>
                <strong>Single Checkout</strong> — You pay once for everything. We handle splitting payments to each seller behind the scenes.
              </li>
            </ol>
            <CoScreenshot isDark={isDark} src="/help/cart-optimized.jpg" alt="Cart optimizer showing best price results" />
          </CoSection>

          <CoSection isDark={isDark} icon="lightbulb-o" title="Tips for Buyers" delay="3">
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '10px' }}>
                <strong>Buy from fewer sellers</strong> — Consolidating your purchases with fewer sellers reduces total shipping costs.
              </li>
              <li style={{ marginBottom: '10px' }}>
                <strong>Look for free shipping</strong> — Sellers offering free shipping are highlighted with a green badge.
              </li>
              <li style={{ marginBottom: '10px' }}>
                <strong>Check seller ratings</strong> — Higher-rated sellers tend to ship faster and package cards more carefully.
              </li>
              <li style={{ marginBottom: '10px' }}>
                <strong>Use the cart page</strong> — The cart page shows your items grouped by seller with a clear shipping breakdown before checkout.
              </li>
            </ul>
          </CoSection>

          {/* CTA */}
          <div className="help-reveal help-fade-up help-delay-4" style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/browse" className="tw-no-underline help-cta-btn" style={{
              display: 'inline-block', backgroundColor: '#c9a84c', color: '#000',
              fontWeight: '600', fontSize: '15px', padding: '12px 32px',
              borderRadius: '10px', textDecoration: 'none'
            }}>
              Start Shopping
            </Link>
          </div>
        </div>

        {this.state.showScrollTop && (
          <button
            onClick={function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default CartOptimizer;
